import { supabase } from '../lib/supabase';
import { emailNotificationService } from './emailNotificationService';

/**
 * Service for managing access requests in Supabase
 * Handles CRUD operations and data transformation between snake_case (DB) and camelCase (React)
 */

// Transform database row (snake_case) to React object (camelCase)
const transformFromDB = (row) => {
  if (!row) return null;
  
  return {
    id: row?.id,
    fullName: row?.full_name,
    email: row?.email,
    role: row?.role,
    bio: row?.bio,
    experience: row?.experience,
    industry: row?.industry,
    seniority: row?.seniority,
    language: row?.language,
    location: row?.location,
    companyName: row?.company_name,
    companyWebsite: row?.company_website,
    linkedinUrl: row?.linkedin_url,
    status: row?.status,
    qualificationScore: row?.qualification_score,
    adminNotes: row?.admin_notes,
    followUpStatus: row?.follow_up_status,
    submittedAt: row?.submitted_at,
    updatedAt: row?.updated_at,
    reviewedAt: row?.reviewed_at,
    reviewedBy: row?.reviewed_by
  };
};

// Transform React object (camelCase) to database format (snake_case)
const transformToDB = (data) => {
  const dbData = {};
  
  if (data?.fullName !== undefined) dbData.full_name = data?.fullName;
  if (data?.email !== undefined) dbData.email = data?.email;
  if (data?.role !== undefined) dbData.role = data?.role;
  if (data?.bio !== undefined) dbData.bio = data?.bio;
  if (data?.experience !== undefined) dbData.experience = data?.experience;
  if (data?.industry !== undefined) dbData.industry = data?.industry;
  if (data?.seniority !== undefined) dbData.seniority = data?.seniority;
  if (data?.language !== undefined) dbData.language = data?.language;
  if (data?.location !== undefined) dbData.location = data?.location;
  if (data?.companyName !== undefined) dbData.company_name = data?.companyName;
  if (data?.companyWebsite !== undefined) dbData.company_website = data?.companyWebsite;
  if (data?.linkedinUrl !== undefined) dbData.linkedin_url = data?.linkedinUrl;
  if (data?.status !== undefined) dbData.status = data?.status;
  if (data?.qualificationScore !== undefined) dbData.qualification_score = data?.qualificationScore;
  if (data?.adminNotes !== undefined) dbData.admin_notes = data?.adminNotes;
  if (data?.followUpStatus !== undefined) dbData.follow_up_status = data?.followUpStatus;
  if (data?.reviewedAt !== undefined) dbData.reviewed_at = data?.reviewedAt;
  if (data?.reviewedBy !== undefined) dbData.reviewed_by = data?.reviewedBy;
  
  return dbData;
};

export const accessRequestService = {
  /**
   * Get all access requests (admin only)
   */
  async getAll() {
    try {
      const { data, error } = await supabase?.from('access_requests')?.select('*')?.order('submitted_at', { ascending: false });
      
      if (error) throw error;
      return data?.map(transformFromDB) || [];
    } catch (error) {
      console.error('Error fetching access requests:', error);
      throw error;
    }
  },

  /**
   * Get only pending access requests using the admin_pending_approvals view
   * This view automatically filters for status = 'pending'
   */
  async getPendingApprovals() {
    try {
      const { data, error } = await supabase
        ?.from('admin_pending_approvals')
        ?.select('*')
        ?.order('submitted_at', { ascending: false });
      
      if (error) throw error;
      return data?.map(transformFromDB) || [];
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      throw error;
    }
  },

  /**
   * Get access request by ID
   */
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('access_requests')?.select('*')?.eq('id', id)?.single();
      
      if (error) throw error;
      return transformFromDB(data);
    } catch (error) {
      console.error('Error fetching access request:', error);
      throw error;
    }
  },

  /**
   * Get access request by email
   */
  async getByEmail(email) {
    try {
      const { data, error } = await supabase?.from('access_requests')?.select('*')?.eq('email', email)?.single();
      
      if (error) {
        if (error?.code === 'PGRST116') return null; // Not found
        throw error;
      }
      return transformFromDB(data);
    } catch (error) {
      console.error('Error fetching access request by email:', error);
      throw error;
    }
  },

  /**
   * Create new access request
   */
  async create(requestData) {
    try {
      const { data, error } = await supabase?.from('access_requests')?.insert([requestData])?.select()?.single();

      if (error) throw error;

      // Send notification to admin about new profile submission
      await emailNotificationService?.sendApprovalNotification({
        adminEmail: 'jonathan@blindrecruit.com',
        adminName: 'Jonathan',
        requestDetails: {
          id: data?.id,
          full_name: data?.full_name,
          email: data?.email,
          role: data?.role,
          status: data?.status,
          company_name: data?.company_name,
          industry: data?.industry,
          seniority: data?.seniority,
          submitted_at: data?.submitted_at
        },
        notificationType: 'new_submission'
      });

      return data;
    } catch (error) {
      console.error('Error creating access request:', error);
      throw error;
    }
  },

  /**
   * Update access request (admin only)
   */
  async update(id, updates) {
    try {
      const dbData = transformToDB(updates);
      
      const { data, error } = await supabase?.from('access_requests')?.update(dbData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      return transformFromDB(data);
    } catch (error) {
      console.error('Error updating access request:', error);
      throw error;
    }
  },

  /**
   * Update status (admin only)
   */
  async updateStatus(id, status, adminNotes = null, reviewedBy = null) {
    try {
      const updateData = {
        status,
        reviewed_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString(),
        last_status_change: new Date()?.toISOString()
      };

      if (adminNotes) updateData.admin_notes = adminNotes;
      if (reviewedBy) updateData.reviewed_by = reviewedBy;

      const { data, error } = await supabase?.from('access_requests')?.update(updateData)?.eq('id', id)?.select()?.single();

      if (error) throw error;

      // Send notification to admin about status change
      await emailNotificationService?.sendApprovalNotification({
        adminEmail: 'jonathan@blindrecruit.com',
        adminName: 'Jonathan',
        requestDetails: {
          id: data?.id,
          full_name: data?.full_name,
          email: data?.email,
          role: data?.role,
          status: data?.status,
          company_name: data?.company_name,
          industry: data?.industry,
          seniority: data?.seniority,
          submitted_at: data?.submitted_at
        },
        notificationType: 'status_change'
      });

      return data;
    } catch (error) {
      console.error('Error updating access request status:', error);
      throw error;
    }
  },

  /**
   * Delete access request (admin only)
   */
  async delete(id) {
    try {
      const { error } = await supabase?.from('access_requests')?.delete()?.eq('id', id);
      
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting access request:', error);
      throw error;
    }
  },

  /**
   * Get requests by status
   */
  async getByStatus(status) {
    try {
      const { data, error } = await supabase?.from('access_requests')?.select('*')?.eq('status', status)?.order('submitted_at', { ascending: false });
      
      if (error) throw error;
      return data?.map(transformFromDB) || [];
    } catch (error) {
      console.error('Error fetching requests by status:', error);
      throw error;
    }
  },

  /**
   * Get requests by role
   */
  async getByRole(role) {
    try {
      const { data, error } = await supabase?.from('access_requests')?.select('*')?.eq('role', role)?.order('submitted_at', { ascending: false });
      
      if (error) throw error;
      return data?.map(transformFromDB) || [];
    } catch (error) {
      console.error('Error fetching requests by role:', error);
      throw error;
    }
  }
};